import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { faBars, faTimes, faUserCog, faLock, faGlobe, faHome, faBook, faCode, faComments, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { theme } from '../styles/GlobalStyles';

// Estilos actualizados para un diseño centrado
const HeaderContainer = styled.header`
  background-color: rgba(46, 46, 46, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 12px 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &.scrolled {
    padding: 8px 0;
    background-color: rgba(46, 46, 46, 0.98);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 0;
    
    &.scrolled {
      padding: 0;
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, 
      rgba(255, 107, 107, 0), 
      rgba(255, 107, 107, 0.5), 
      rgba(255, 107, 107, 0)
    );
    z-index: 101;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: center; /* Centrar el contenido */
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 0 15px; /* Reducir el padding */
    justify-content: flex-start; /* Alinear a la izquierda en móviles */
    height: 56px; /* Altura fija para la barra de navegación móvil */
  }
`;

// Logo para la versión móvil
const MobileLogo = styled.div`
  display: none;
  font-weight: 700;
  font-size: 1.2rem;
  color: ${theme.colors.primary};
  
  @media (max-width: 768px) {
    display: block;
    padding: 8px 0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
`;

// Estilos para la navegación - Corregido para móviles
const Nav = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    position: fixed;
    top: ${props => props.headerHeight || 0}px;
    left: 0;
    right: 0;
    width: 100%;
    height: calc(100vh - ${props => props.headerHeight || 0}px);
    background-color: rgba(46, 46, 46, 0.98);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    flex-direction: column;
    justify-content: flex-start; /* Alinear desde arriba */
    z-index: 90;
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease;
    transform: translateY(${props => props.isOpen ? '0' : '-110%'});
    opacity: ${props => props.isOpen ? 1 : 0};
    padding: 30px 0;
    overflow-y: auto;
  }
`;

// Lista de navegación
const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 5px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: center;
    width: 100%;
    padding: 20px 0;
    margin-top: 20px;
  }
`;

// Elemento de navegación
const NavItem = styled.li`
  position: relative;
  margin: 0 5px;
  
  @media (max-width: 768px) {
    margin: 0;
    width: 85%;
    text-align: center;
  }
`;

// Enlace de navegación con indicador activo
const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  color: ${props => props.active ? theme.colors.primary : theme.colors.secondary};
  text-decoration: none;
  font-weight: ${props => props.active ? 600 : 500};
  font-size: 0.95rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%) scaleX(${props => props.active ? 1 : 0});
    width: 20px;
    height: 3px;
    background-color: ${theme.colors.primary};
    border-radius: 2px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: ${theme.colors.primary};
    background-color: rgba(255, 107, 107, 0.08);
    
    &::after {
      transform: translateX(-50%) scaleX(0.8);
    }
  }
  
  svg {
    margin-right: 8px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 14px 16px;
    justify-content: center;
    font-size: 1.1rem;
    background-color: ${props => props.active ? 'rgba(255, 107, 107, 0.08)' : 'transparent'};
    
    &::after {
      bottom: -2px;
      width: 30px;
    }
  }
`;

// Botón de Discord
const DiscordButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background-color: #5865F2;
  color: white;
  text-decoration: none;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  
  &:hover {
    background-color: #4752c4;
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 8px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 14px 16px;
    margin-top: 5px;
    font-size: 1.1rem;
    border-radius: 8px;
  }
`;

// Botón del menú móvil - Mejorado
const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: ${theme.colors.secondary};
  font-size: 1.8rem;
  cursor: pointer;
  z-index: 102;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    padding: 0;
    font-size: 1.4rem;
    background-color: ${props => props.isOpen ? 'transparent' : 'rgba(46, 46, 46, 0.9)'};
    backdrop-filter: ${props => props.isOpen ? 'none' : 'blur(8px)'};
    -webkit-backdrop-filter: ${props => props.isOpen ? 'none' : 'blur(8px)'};
    border-radius: 8px;
    transition: all 0.3s ease;
    border: ${props => props.isOpen ? 'none' : '1px solid rgba(255, 107, 107, 0.3)'};
    color: ${props => props.isOpen ? theme.colors.primary : theme.colors.secondary};
  }
  
  &:hover {
    color: ${theme.colors.primary};
  }
  
  &:focus {
    outline: none;
  }
`;

// Espaciador para compensar el header fijo
const HeaderSpacer = styled.div`
  height: ${props => props.height || 0}px;
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [headerRef, setHeaderRef] = useState(null);
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    if (headerRef) {
      const updateHeaderHeight = () => {
        setHeaderHeight(headerRef.offsetHeight);
      };
      
      // Actualizar la altura inicialmente
      updateHeaderHeight();
      
      // Actualizar la altura cuando cambia el tamaño de la ventana
      const handleResize = () => {
        updateHeaderHeight();
        
        // Auto-cerrar el menú si se cambia a pantalla grande
        if (window.innerWidth > 768 && isMenuOpen) {
          setIsMenuOpen(false);
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [headerRef, scrolled, isMenuOpen]);
  
  useEffect(() => {
    // Bloquear el scroll del body cuando el menú móvil está abierto
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Cerrar el menú cuando cambia la ruta
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);
  
  // Determinar si una ruta está activa
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Manejar clic en enlace en versión móvil
  const handleLinkClick = () => {
    if (window.innerWidth <= 768) {
      setIsMenuOpen(false);
    }
  };

  // Controlador para cerrar menú al hacer clic fuera
  const handleOutsideClick = (e) => {
    if (isMenuOpen && e.target.closest('nav') === null && !e.target.closest('button')) {
      setIsMenuOpen(false);
    }
  };

  // Agregar evento para cerrar menú al hacer clic fuera
  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('click', handleOutsideClick);
    }
    
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isMenuOpen]);

  return (
    <>
      <HeaderContainer 
        className={scrolled ? 'scrolled' : ''} 
        ref={setHeaderRef}
      >
        <HeaderContent>
          <MobileLogo>TuSecreto</MobileLogo>
          
          <MobileMenuButton 
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }} 
            aria-label="Menú"
            aria-expanded={isMenuOpen}
            isOpen={isMenuOpen}
          >
            <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
          </MobileMenuButton>
          
          <Nav isOpen={isMenuOpen} headerHeight={headerHeight}>
            <NavList>
              <NavItem>
                <NavLink to="/secretos" active={isActive('/secretos')} onClick={handleLinkClick}>
                  <FontAwesomeIcon icon={faGlobe} /> Secretos
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/inicio" active={isActive('/inicio')} onClick={handleLinkClick}>
                  <FontAwesomeIcon icon={faHome} /> Inicio
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/comandos" active={isActive('/comandos')} onClick={handleLinkClick}>
                  <FontAwesomeIcon icon={faCode} /> Comandos
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/guia" active={isActive('/guia')} onClick={handleLinkClick}>
                  <FontAwesomeIcon icon={faBook} /> Guía
                </NavLink>
              </NavItem>
              <NavItem>
                <DiscordButton 
                  href="https://discord.gg/pxHEVKftmg" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={handleLinkClick}
                >
                  <FontAwesomeIcon icon={faDiscord} /> <span>Comunidad</span>
                </DiscordButton>
              </NavItem>
              {user && (
                <NavItem>
                  <NavLink to="/admin" active={isActive('/admin')} onClick={handleLinkClick}>
                    <FontAwesomeIcon icon={faLock} /> Panel
                  </NavLink>
                </NavItem>
              )}
            </NavList>
          </Nav>
        </HeaderContent>
      </HeaderContainer>
      <HeaderSpacer height={headerHeight} />
    </>
  );
};

export default Header; 